import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ArrowDownToLine, Calendar, Plus } from "lucide-react";
import { DateMarker } from "components/charts";
import { CountBadge } from "components/indicators/CountBadge";
import type { MetadataEvent } from "../../hooks/useMetadataEvents";
import { hasAdoptedContent } from "./OnixImportTooltipContent";
import { MetadataEventMarkerTooltip } from "./MetadataEventMarkerTooltip";

export const MetadataEventMarker = ({
  xValue,
  events,
}: {
  xValue: string;
  events: MetadataEvent[];
}) => {
  const { t } = useTranslation(["productDetails"]);
  const [first] = events;
  const count = events.length;

  if (first == null) {
    return null;
  }

  const stackAriaLabel = (title: string) =>
    count > 1
      ? t("productDetails:salesData.events.marker.stackAriaLabel", {
          title,
          count,
          date: format(first.occurredAt, "d MMM yyyy"),
        })
      : undefined;

  switch (first.type) {
    case "ONIX_IMPORT":
      return (
        <DateMarker
          xValue={xValue}
          className={
            events.some(
              (event) =>
                event.type === "ONIX_IMPORT" && hasAdoptedContent(event),
            )
              ? "text-badge-success"
              : "text-secondary-400"
          }
          icon={
            <CountBadge count={count > 1 ? count : null}>
              <ArrowDownToLine className="h-4 w-4" />
            </CountBadge>
          }
          label={t("productDetails:salesData.events.onixImport.label")}
          title={t("productDetails:salesData.events.onixImport.badge")}
          ariaLabel={stackAriaLabel(
            t("productDetails:salesData.events.onixImport.badge"),
          )}
          date={first.occurredAt}
          description={<MetadataEventMarkerTooltip events={events} />}
        />
      );
    case "GENERATION":
      return (
        <DateMarker
          xValue={xValue}
          className="text-primary"
          icon={
            <CountBadge count={count > 1 ? count : null}>
              <Plus className="h-4 w-4" />
            </CountBadge>
          }
          label={t("productDetails:salesData.events.generation.label")}
          title={t("productDetails:salesData.events.generation.badge")}
          ariaLabel={stackAriaLabel(
            t("productDetails:salesData.events.generation.badge"),
          )}
          date={first.occurredAt}
          description={<MetadataEventMarkerTooltip events={events} />}
        />
      );
    case "RELEASE_DATE":
      return (
        <DateMarker
          xValue={xValue}
          className="text-purple-700"
          icon={
            <CountBadge count={count > 1 ? count : null}>
              <Calendar className="h-3.5 w-3.5" />
            </CountBadge>
          }
          label={t("productDetails:salesData.events.releaseDate.label")}
          title={t("productDetails:salesData.events.releaseDate.title")}
          date={first.occurredAt}
          description={<MetadataEventMarkerTooltip events={events} />}
        />
      );
    default:
      throw new Error(`Unhandled metadata event type: ${first.type}`);
  }
};
